// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Tithe — the board and the toll cards. DOM for the board (crisp text, real buttons, free
// accessibility, trivially responsive) with one canvas overlay for particles and shake.
//
// TWO ORIENTATION RULES, and both matter more than they look:
//
//   1. THE BOARD IS ALWAYS DRAWN FROM THE LOCAL PLAYER'S SIDE. Seat 1 sees the board rotated 180°
//      so its own pieces are on the bottom row. Without this, half of all players read every toll
//      card upside down relative to what they are looking at.
//   2. A TOLL CARD IS READ FROM ITS OWNER'S SIDE. Your own cards are drawn with forward = up. The
//      opponent's are drawn ROTATED, because their forward is down the screen from where you sit —
//      so the marks on their cards show where THEY can go, in the board's actual directions, not
//      where you could go with the same card. Drawing their card in your orientation would be a
//      confident lie in the one place a player is trying to work out what is about to happen.

import { NC, W, cx, cy } from './rules';
import { tollOf, type Toll } from './tolls';

export interface LastMove {
  from: number;
  to: number;
  seat: number;
}

export interface ViewHints {
  /** Board-space cells that are legal destinations for a quiet move. */
  moveTargets: Set<number>;
  /** Board-space cells that are legal destinations which take a piece. */
  takeTargets: Set<number>;
  /** The piece the player has picked up. */
  selected: number | null;
  /** Cells holding a piece of the side to move that has at least one legal move. */
  movable: Set<number>;
  last: LastMove | null;
  mySeat: number | null;
  interactive: boolean;
  winCell: number | null;
}

export const emptyHints = (): ViewHints => ({
  moveTargets: new Set(),
  takeTargets: new Set(),
  selected: null,
  movable: new Set(),
  last: null,
  mySeat: null,
  interactive: false,
  winCell: null,
});

/** Board cell -> the screen slot it is drawn in. A 180° rotation when the local player is seat 1. */
export const viewCell = (cell: number, flip: boolean): number => (flip ? NC - 1 - cell : cell);

export interface BoardState {
  occ: Int8Array;
  pos: Int8Array;
  coffers: readonly number[];
  keeperOf: (p: number) => boolean;
  seatOf: (p: number) => number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  colour: string;
}

const MAX_PARTICLES = 220;

export class BoardView {
  readonly root: HTMLElement;
  private readonly board: HTMLElement;
  private readonly cells: HTMLButtonElement[] = [];
  private readonly fx: HTMLCanvasElement;
  private readonly particles: Particle[] = [];
  private raf = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private flip = false;
  private destroyed = false;

  constructor(
    host: HTMLElement,
    private readonly onCell: (cell: number) => void,
    private readonly reducedMotion: boolean,
  ) {
    this.root = host;
    host.innerHTML = '';
    this.board = document.createElement('div');
    this.board.className = 'board';
    this.board.setAttribute('role', 'grid');
    this.board.setAttribute('aria-label', 'The court');
    for (let slot = 0; slot < NC; slot++) {
      const b = document.createElement('button');
      b.className = 'cell';
      b.type = 'button';
      b.dataset.slot = String(slot);
      b.innerHTML = '<i class="mark"></i>';
      b.addEventListener('click', () => {
        const s = Number(b.dataset.slot);
        this.onCell(viewCell(s, this.flip));
      });
      this.cells.push(b);
      this.board.appendChild(b);
    }
    host.appendChild(this.board);

    this.fx = document.createElement('canvas');
    this.fx.className = 'fx';
    this.fx.setAttribute('aria-hidden', 'true');
    host.appendChild(this.fx);
  }

  setFlip(flip: boolean): void {
    this.flip = flip;
  }

  paint(s: BoardState, h: ViewHints): void {
    if (this.destroyed) return;
    for (let cell = 0; cell < NC; cell++) {
      const el = this.cells[viewCell(cell, this.flip)];
      const cls = ['cell'];
      if (s.coffers.includes(cell)) cls.push('coffer');
      if (h.last && h.last.from === cell) cls.push('last-from');
      if (h.last && h.last.to === cell) cls.push('last-to');
      if (h.moveTargets.has(cell)) cls.push('t-move');
      if (h.takeTargets.has(cell)) cls.push('t-take');
      if (h.selected === cell) cls.push('picked');
      if (h.movable.has(cell)) cls.push('movable');
      if (h.winCell === cell) cls.push('t-win');
      el.className = cls.join(' ');

      const p = s.occ[cell];
      const tok = el.querySelector('.tok');
      if (p < 0) {
        tok?.remove();
      } else {
        const seat = s.seatOf(p);
        const want = `tok p${seat + 1}${s.keeperOf(p) ? ' beacon' : ''}`;
        if (!tok || tok.className !== want) {
          tok?.remove();
          const t = document.createElement('i');
          t.className = want;
          t.innerHTML = '<i class="ring"></i>';
          el.appendChild(t);
        }
      }
      el.disabled = !h.interactive;
      el.setAttribute(
        'aria-label',
        `${String.fromCharCode(97 + cx(cell))}${W - cy(cell)}${p < 0 ? ' empty' : s.keeperOf(p) ? ` keeper ${s.seatOf(p) === 0 ? 'ochre' : 'indigo'}` : ` bearer ${s.seatOf(p) === 0 ? 'ochre' : 'indigo'}`}`,
      );
    }
  }

  nudge(cell: number): void {
    const el = this.cells[viewCell(cell, this.flip)];
    el.classList.remove('nope');
    void el.offsetWidth;
    el.classList.add('nope');
  }

  shake(px: number): void {
    if (this.reducedMotion) return;
    this.board.style.setProperty('--shake', `${px}px`);
    this.board.classList.remove('shaking');
    void this.board.offsetWidth;
    this.board.classList.add('shaking');
  }

  flash(): void {
    this.board.classList.add('flash');
    this.timer = setTimeout(() => this.board.classList.remove('flash'), 700);
  }

  burst(cell: number, colour: string, n: number): void {
    if (this.reducedMotion) return;
    const rect = this.board.getBoundingClientRect();
    if (rect.width < 4) return; // a backgrounded tab measures 0 and would spray the corner
    const slot = viewCell(cell, this.flip);
    const cr = this.cells[slot].getBoundingClientRect();
    const x = cr.left - rect.left + cr.width / 2;
    const y = cr.top - rect.top + cr.height / 2;
    for (let i = 0; i < n && this.particles.length < MAX_PARTICLES; i++) {
      const a = (Math.PI * 2 * i) / n + Math.random() * 0.5;
      const sp = 40 + Math.random() * 110;
      this.particles.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 20, life: 1, colour });
    }
    this.start();
  }

  private start(): void {
    if (this.raf !== 0 || this.destroyed) return;
    let last = Date.now();
    const tick = (): void => {
      this.raf = 0;
      if (this.destroyed) return;
      const now = Date.now();
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const rect = this.board.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      if (rect.width > 4) {
        if (this.fx.width !== Math.round(rect.width * dpr)) {
          this.fx.width = Math.round(rect.width * dpr);
          this.fx.height = Math.round(rect.height * dpr);
        }
        const ctx = this.fx.getContext('2d');
        if (ctx) {
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          ctx.clearRect(0, 0, rect.width, rect.height);
          for (const p of this.particles) {
            ctx.globalAlpha = Math.max(0, p.life);
            ctx.fillStyle = p.colour;
            ctx.beginPath();
            ctx.arc(p.x, p.y, 3.2 * p.life + 0.6, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.globalAlpha = 1;
        }
      }
      for (let i = this.particles.length - 1; i >= 0; i--) {
        const p = this.particles[i];
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vy += 340 * dt;
        p.life -= dt * 1.5;
        if (p.life <= 0) this.particles.splice(i, 1);
      }
      if (this.particles.length > 0) this.raf = requestAnimationFrame(tick);
      else {
        const ctx = this.fx.getContext('2d');
        ctx?.clearRect(0, 0, this.fx.width, this.fx.height);
      }
    };
    this.raf = requestAnimationFrame(tick);
  }

  destroy(): void {
    this.destroyed = true;
    if (this.raf !== 0) cancelAnimationFrame(this.raf);
    if (this.timer !== null) clearTimeout(this.timer);
    this.particles.length = 0;
    this.root.innerHTML = '';
  }
}

// ── toll cards ──────────────────────────────────────────────────────────────────
export interface TollCardOpts {
  /** Rotate the printed offsets 180°, for a card belonging to the player on the far side. */
  flip?: boolean;
  klass?: string;
  /** Rendered as a <button> with this data attribute rather than a <div>. */
  action?: string;
  selected?: boolean;
  disabled?: boolean;
  label?: string;
}

/**
 * One toll, as a 5×5 dot grid with the origin marked in the middle. A grid, not an arrow soup:
 * you are matching it against a 5×5 board, so the shapes line up one-for-one and reading a card is
 * the same act as reading the board.
 */
export function tollHtml(t: Toll | number, o: TollCardOpts = {}): string {
  const toll = typeof t === 'number' ? tollOf(t) : t;
  const marked = new Set<number>();
  for (const [dx, dy] of toll.off) {
    const sx = o.flip ? -dx : dx;
    const sy = o.flip ? dy : -dy; // screen y grows downward, card dy grows forward
    const x = 2 + sx;
    const y = 2 + sy;
    if (x < 0 || x > 4 || y < 0 || y > 4) continue;
    marked.add(y * 5 + x);
  }
  let grid = '';
  for (let i = 0; i < 25; i++) grid += `<i class="${i === 12 ? 'o' : marked.has(i) ? 'x' : ''}"></i>`;
  const inner = `<span class="tgrid" aria-hidden="true">${grid}</span><b class="tname">${toll.name}</b>`;
  const cls = `toll${o.selected ? ' on' : ''}${o.klass ? ` ${o.klass}` : ''}`;
  const aria = o.label ?? `${toll.name}${o.flip ? ', opponent' : ''}`;
  if (o.action !== undefined) {
    return `<button type="button" class="${cls}" data-toll="${o.action}" aria-pressed="${!!o.selected}" aria-label="${aria}"${o.disabled ? ' disabled' : ''}>${inner}</button>`;
  }
  return `<div class="${cls}" role="img" aria-label="${aria}">${inner}</div>`;
}
